import { Identifier } from 'src/types/utilityTypes';
import api from 'src/utils/ajax';

import {
  CreateLocationTaggingRequest,
  CreateTagRequest,
  CreateWorkoutPlanCollectionTaggingPayload,
  CreateWorkoutPlanCollectionTaggingRequest,
  FetchLocationTaggingsRequest,
  FetchTagsByTypeRequest,
  FetchWorkoutPlanCollectionTaggingsRequest,
  RawTag,
  RawTagging,
  RawTaggingsIndexResponse,
  RawTagsIndexResponse,
  Tag,
  Tagging,
  UpdateTagRequest,
  UpdateWorkoutPlanTagRequest,
} from './_types';
import {
  extractRawTaggings,
  normalizeTag,
  normalizeTagging,
  normalizeTags,
} from './normalizers';

export type {
  CreateWorkoutPlanCollectionTaggingPayload,
  Tag,
  Tagging,
} from './_types';

export const fetchTagsByType = async (type: string): Promise<Tag[]> => {
  const params: FetchTagsByTypeRequest = {
    fetch_all: true,
    type,
  };

  const { data } = await api.get<RawTagsIndexResponse>('/api/v4/tags', {
    params,
  });

  return normalizeTags(data);
};

export const workoutPlanTags = async (): Promise<Tag[]> => {
  return fetchTagsByType(
    'workout-plan,difficulty,equipment,badge,workout-time,frequency,objective,alternate-video'
  );
};

export const createTag = async (tag: Partial<Tag>): Promise<Tag> => {
  const body: CreateTagRequest = {
    name: tag.name,
    tag_type: tag.tagType,
    ...(tag.description != null ? { description: tag.description } : {}),
    ...(tag.collectionImageId !== undefined
      ? { collection_image_id: tag.collectionImageId }
      : {}),
    ...(tag.image ? { image: tag.image } : {}),
    ...(tag.originalName ? { original_name: tag.originalName } : {}),
  };

  const { data } = await api.post<RawTag>('/api/v4/tags', body);
  return normalizeTag(data);
};

export const fetchLocationTaggings = async (
  locationId: Identifier
): Promise<Tagging[]> => {
  const params: FetchLocationTaggingsRequest = {
    taggable_id: locationId,
    taggable_type: 'Fbm::Location',
    tag_type: 'location',
  };

  const { data } = await api.get<RawTagging[] | RawTaggingsIndexResponse>(
    '/api/v4/taggings',
    { params }
  );

  return extractRawTaggings(data).map((raw) =>
    normalizeTagging(raw, {
      taggableId: locationId,
      taggableType: 'Fbm::Location',
    })
  );
};

export const createLocationTagging = async (
  locationId: Identifier,
  tagId: Identifier
): Promise<Tagging> => {
  const body: CreateLocationTaggingRequest = {
    tagging: {
      taggable_id: locationId,
      taggable_type: 'Fbm::Location',
      tag_id: tagId,
    },
  };

  const { data } = await api.post<RawTagging>('/api/v4/taggings', body);

  return normalizeTagging(data, {
    taggableId: locationId,
    taggableType: 'Fbm::Location',
  });
};

export const deleteLocationTagging = async (
  taggingId: Identifier
): Promise<void> => {
  await api.delete(`/api/v4/taggings/${taggingId}`);
};

export const updateTag = async (tag: Partial<Tag>): Promise<Tag> => {
  const body: UpdateTagRequest = {
    name: tag.name,
    original_name: tag.originalName,
    taggable_type: tag.tagType,
    ...(tag.image ? { image: tag.image } : {}),
  };

  const { data } = await api.post<RawTag>('/api/v3/tags/update_tag/', body);
  return normalizeTag(data);
};

export const updateWorkoutPlanTag = async (
  id: Identifier,
  tag: Partial<Tag>
): Promise<Tag> => {
  const body: UpdateWorkoutPlanTagRequest = {
    name: tag.name,
    tag_type: tag.tagType,
    ...(tag.description != null ? { description: tag.description } : {}),
    ...(tag.collectionImageId !== undefined
      ? { collection_image_id: tag.collectionImageId }
      : {}),
  };

  const { data } = await api.put<RawTag>(`/api/v4/tags/${id}/`, body);
  return normalizeTag(data);
};

export const deleteTagByName = async (
  tag_name: string,
  type: string
): Promise<void> => {
  await api.post(`/api/v3/tags/delete_tag/`, {
    name: tag_name,
    taggable_type: type,
  });
};

export const deleteTag = async (id: Identifier): Promise<void> => {
  await api.delete(`/api/v4/tags/${id}/`);
};

export const fetchCollectionTags = async (): Promise<Tag[]> => {
  return fetchTagsByType('collection');
};

export const fetchWorkoutPlanCollectionTaggings = async (
  planId: Identifier
): Promise<Tagging[]> => {
  const params: FetchWorkoutPlanCollectionTaggingsRequest = {
    fetch_all: true,
    tag_type: 'collection',
    taggable_id: planId,
    taggable_type: 'WorkoutPlan',
  };

  const { data } = await api.get<RawTagging[] | RawTaggingsIndexResponse>(
    '/api/v4/taggings',
    { params }
  );

  return extractRawTaggings(data).map((raw) =>
    normalizeTagging(raw, {
      taggableId: planId,
      taggableType: 'WorkoutPlan',
    })
  );
};

export const createWorkoutPlanCollectionTagging = async ({
  planId,
  sortOrder = 0,
  tagId,
}: CreateWorkoutPlanCollectionTaggingPayload): Promise<Tagging> => {
  const body: CreateWorkoutPlanCollectionTaggingRequest = {
    tagging: {
      sort_order: sortOrder,
      tag_id: tagId,
      taggable_id: planId,
      taggable_type: 'WorkoutPlan',
    },
  };

  const { data } = await api.post<RawTagging>('/api/v4/taggings', body);

  return normalizeTagging(data, {
    taggableId: planId,
    taggableType: 'WorkoutPlan',
  });
};

export const deleteTagging = async (taggingId: Identifier): Promise<void> => {
  await api.delete(`/api/v4/taggings/${taggingId}`);
};

export const fetchTaggingsByTagId = async (
  tagId: Identifier
): Promise<Tagging[]> => {
  const { data } = await api.get<RawTagging[] | RawTaggingsIndexResponse>(
    '/api/v4/taggings',
    {
      params: {
        fetch_all: true,
        tag_id: tagId,
      },
    }
  );

  return extractRawTaggings(data).map((raw) => normalizeTagging(raw));
};

export const updateTaggingSortOrder = async (
  taggingId: Identifier,
  sortOrder: number
): Promise<Tagging> => {
  const { data } = await api.put<RawTagging>(`/api/v4/taggings/${taggingId}`, {
    tagging: { sort_order: sortOrder },
  });

  return normalizeTagging(data);
};

export const uploadCollectionImage = async (
  file: File
): Promise<{ id: Identifier; url: string }> => {
  const formData = new FormData();
  formData.append('asset_image[image]', file);
  const { data } = await api.post<{ id: Identifier; url: string }>(
    '/api/v2/assets/image/collection',
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }
  );
  return { id: data.id, url: data.url };
};
